import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const subscriptionTiers = [
  {
    id: "basic",
    name: "Basic",
    price: 9.99,
    features: [
      "Basic property management assistance",
      "Simple guest communication templates",
      "Limited AI interactions per day",
    ],
  },
  {
    id: "premium",
    name: "Professional",
    price: 29.99,
    features: [
      "Advanced property optimization suggestions",
      "Custom communication templates",
      "Unlimited AI interactions",
      "Market analysis insights",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 99.99,
    features: [
      "Full property portfolio management",
      "Advanced analytics and reporting",
      "Priority AI support",
      "Custom integrations",
      "Dedicated account manager",
    ],
  },
] as const;

type SubscriptionTier = typeof subscriptionTiers[number]['id'];

export function SubscriptionManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  const { data: currentSubscription, isLoading } = useQuery({
    queryKey: ['ai-subscription'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from('ai_assistant_subscriptions')
        .select('*')
        .eq('host_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  const subscribeMutation = useMutation({
    mutationFn: async (tier: SubscriptionTier) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Calculate expiry date (30 days from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      const { error } = await supabase
        .from('ai_assistant_subscriptions')
        .upsert({
          host_id: user.id,
          tier,
          is_active: true,
          expires_at: expiresAt.toISOString(),
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-subscription'] });
      toast({
        title: "Subscription Updated",
        description: "Your AI Assistant subscription has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update subscription. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSubscribe = async (tier: SubscriptionTier) => {
    setSelectedTier(tier);
    try {
      await subscribeMutation.mutateAsync(tier);
    } finally {
      setSelectedTier(null);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-4">Loading subscription details...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI Assistant Subscription</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 md:grid-cols-3">
          {subscriptionTiers.map((tier) => (
            <Card key={tier.id} className={cn(
              "relative overflow-hidden transition-all",
              currentSubscription?.tier === tier.id && "border-primary"
            )}>
              <CardHeader>
                <CardTitle>{tier.name}</CardTitle>
                <p className="text-2xl font-bold">${tier.price}/month</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-sm">
                      <span className="mr-2">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={currentSubscription?.tier === tier.id ? "outline" : "default"}
                  disabled={selectedTier === tier.id}
                  onClick={() => handleSubscribe(tier.id)}
                >
                  {selectedTier === tier.id ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing
                    </>
                  ) : currentSubscription?.tier === tier.id ? (
                    "Current Plan"
                  ) : (
                    "Subscribe"
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}